import React, { useRef } from "react";
import {
  Grid,
  TextField,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableRow,
  TableCell,
  Autocomplete,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import { Icon as OpusIcon } from "../common/opus";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadIcon from "@mui/icons-material/Download";

const CaptionItem = ({
  caption,
  index,
  isEditing,
  onCaptionChange,
  onFileChange,
  onRemoveCaption,
  onEditCaptionDetail,
  onDownloadCaption,
  onToggleDisabled,
}) => {
  const extractVersions = () => {
    const versionSet = new Set();
    if (caption.all_versions && Array.isArray(caption.all_versions)) {
      caption.all_versions.forEach((item) => {
        if (typeof item === "string") {
          versionSet.add(item);
        } else if (item && typeof item === "object" && item.version) {
          versionSet.add(item.version);
        }
      });
    }

    if (caption.all_captions && Array.isArray(caption.all_captions)) {
      caption.all_captions.forEach((item) => {
        if (item && item.version) {
          versionSet.add(item.version);
        }
      });
    }
    return Array.from(versionSet).sort();
  };

  const versionOptions = extractVersions();
  const inputRef = useRef(null);

  return (
    <>
      <Grid container spacing={1} alignItems="center" sx={{ mt: 0, background: caption.disabled ? 'rgba(252,193,68,0.06)' : 'var(--surface-0)', border: caption.disabled ? '1px solid rgba(252,193,68,0.28)' : '1px solid var(--border)', p: '10px 12px', borderRadius: 'var(--r-md)', opacity: caption.disabled ? 0.6 : 1, transition: 'opacity .15s, border-color .15s' }}>

        {onToggleDisabled && (
          <Grid item size="auto">
            <Tooltip title={caption.disabled ? "Caption disabled — click to enable" : "Disable caption (hidden from renderers)"}>
              <IconButton size="small" onClick={onToggleDisabled} sx={{ color: caption.disabled ? 'var(--amber)' : 'var(--text-dim)', '&:hover': { color: 'var(--amber)' } }}>
                <OpusIcon name={caption.disabled ? "eyeOff" : "eye"} size={18} />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        <Grid item size={3}>
          <TextField
            label="Graphic Name"
            value={caption.caption_name || ""}
            onChange={(e) =>
              onCaptionChange(index, "caption_name", e.target.value)
            }
            fullWidth
            disabled={isEditing && caption.existingFile}
            size={'small'}
          />
        </Grid>

        <Grid item size={3}>
          <TextField
            label="Description"
            value={caption.description || ""}
            onChange={(e) =>
              onCaptionChange(index, "description", e.target.value)
            }
            fullWidth
            size={'small'}
          />
        </Grid>

        <Grid item size={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            {caption.existingFile && !caption.file ? (
              <>
                <input
                  type="file"
                  accept=".zip"
                  hidden
                  ref={inputRef}
                  onChange={(e) => onFileChange(index, e.target.files[0])}
                />
                <Tooltip title="Upload">
                  <IconButton
                    variant="outlined"
                    size="small"
                    color="seafoamGreen"
                    sx={{ mr: 1 }}
                    onClick={() => inputRef.current?.click()}
                  >
                    <CloudUploadIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" display="block">
                  {`${caption.caption_name}.zip`}
                </Typography>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept=".zip"
                  hidden
                  ref={inputRef}
                  onChange={(e) => onFileChange(index, e.target.files[0])}
                />
                <Tooltip title="Upload">
                  <IconButton
                    variant="outlined"
                    size="small"
                    color="seafoamGreen"
                    sx={{ mr: 1 }}
                    onClick={() => inputRef.current?.click()}
                  >
                    <CloudUploadIcon />
                  </IconButton>
                </Tooltip>

                {caption.file ? (
                  <Chip
                    label={caption.file.name}
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="caption" display="block">No file selected</Typography>
                )}
              </>
            )}
          </Box>
        </Grid>

        <Grid item size={1.5}>
          <Autocomplete
            freeSolo
            size="small"
            options={versionOptions}
            value={caption.version || ""}
            onChange={(event, newValue) => {
              onCaptionChange(index, "version", newValue);
            }}
            onInputChange={(event, newInputValue) => {
              onCaptionChange(index, "version", newInputValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Version" />
            )}
          />

          {caption.previousVersion && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {`Adding new version: ${caption.previousVersion} → ${caption.version}`}
            </Typography>
          )}
        </Grid>

        {/* OPUS-1045: pin the row actions flush to the right edge. size="auto"
            takes them out of the 12-col budget (so the optional disable-toggle
            column no longer overflows the row) and ml:'auto' absorbs the free
            space to push the icon cluster to the far right. */}
        <Grid item size="auto" sx={{ display: 'flex', justifyContent: 'end', gap: 1, ml: 'auto', flexShrink: 0 }}>
          {(isEditing || caption.existingFile) && !!onEditCaptionDetail && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => onEditCaptionDetail(caption, index)}
                color="info"
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {caption.existingFile && !!onDownloadCaption && (
            <IconButton
              onClick={() => onDownloadCaption(caption, index)}
              color="info"
              size="small"
              title="Download caption"
            >
              <DownloadIcon />
            </IconButton>
          )}
          <Tooltip title="Delete">
            <IconButton onClick={() => onRemoveCaption(index)} color="info" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default CaptionItem;
